import type { TLocalityTypes } from 'utils/constants.ts';
import type { PageType, PrevPageType } from 'lib/dot/enum/pageType';
import omit from 'lodash/omit';
import pick from 'lodash/pick';
import { SessionStorage } from 'utils/SessionStorage.ts';
import type { OfferDetailMeta } from './offerDetail/utils';
import isArray from 'lodash/isArray';

/**
 * Basic locality data abridged for analytics
 */
export type LocalityAnalytics = {
	localityType: TLocalityTypes;
	regionId: number;
	districtId: number;
	muniId: number;
};

/**
 * Extended locality data for analytics where more info about locality is needed. Used for
 */
export type LocationAnalytics = {
	localityType: TLocalityTypes;
	regionId: number;
	districtId: number;
	muniId: number;
	main: boolean;
	matched: boolean;
};

/**
 * Wrapper around globalThis.window.DOT object.
 */
class DOTClass {
	store: Record<string, any> = {};
	prevPageType: PrevPageType = '';
	pageType: PageType | undefined = undefined;

	initPageType(pageType: PageType) {
		const prevPageType = undefined;

		if (history.state.pageType === undefined) {
			const prevPageType = window.historyStorage.seekHistory(-1)?.pageType ?? undefined;
			this.pageType = pageType;
			this.prevPageType = prevPageType;
			history.replaceState({ ...history.state, pageType, prevPageType }, '');
		} else {
			this.pageType = history.state.pageType;
			this.prevPageType = history.state.prevPageType;
		}
		this.setStore({ prevPageType, pageType });
	}

	/**
	 * @param data
	 * @param useStore Array of keys to pick from the store or boolean to use the whole store
	 * @param omitKeys Change the `pick` behavior to `omit` when using store
	 */
	hit(data: Record<string, any> & { action: string }, useStore?: string[] | boolean, omitKeys?: boolean) {
		let _data = data;
		if (isArray(useStore)) {
			const operation = omitKeys ? omit : pick;
			_data = { ...operation(this.store, useStore), ...data };
		} else if (useStore) {
			_data = { ...this.store, ...data };
		}

		// #if [DEV]
		// Todo: remove after all analytics are implemented, keep this here for easier debug for now
		// eslint-disable-next-line
		console.info(`DOT Hit to be sent:`, _data);
		// #endif

		return new Promise<void>((resolve) => {
			if (!globalThis?.window?.DOT?.configured) {
				globalThis?.addEventListener('dot-cfg', () => {
					globalThis.window.DOT?.hit('event', { d: _data }, () => {
						resolve();
					});
				});
			} else {
				globalThis.window.DOT?.hit('event', { d: _data }, () => {
					resolve();
				});
			}
		});
	}

	/**
	 * @param data
	 * @param callback
	 * @param useStore Array of keys to pick from the store or boolean to use the whole store
	 * @param omitKeys Change the `pick` behavior to `omit` when using store
	 */
	impress(data: Record<string, any>, callback = null, useStore?: string[] | boolean, omitKeys?: boolean) {
		let _data = data;
		if (isArray(useStore)) {
			const operation = omitKeys ? omit : pick;
			_data = { ...operation(this.store, useStore), ...data };
		} else if (useStore) {
			_data = { ...this.store, ...data };
		}

		// #if [DEV]
		// Todo: remove after all analytics are implemented, keep this here for easier debug for now
		// eslint-disable-next-line
		console.info(`DOT Impress to be sent:`, _data);
		// #endif

		if (!globalThis?.window?.DOT?.configured) {
			globalThis?.addEventListener('dot-cfg', () => {
				globalThis.window.DOT?.impress(callback, data);
			});
			return;
		}

		globalThis.window.DOT?.impress(callback, data);
	}

	setStore(data: Record<string, any>) {
		this.store = { ...data, prevPageType: this.prevPageType, pageType: this.pageType };
	}

	addToStore(data: Record<string, any>) {
		this.store = { ...this.store, ...data };
	}

	getStore() {
		return this.store;
	}

	/**
	 * Special utility method. Since the analytics are mainly runtime-based and cannot be fully validated just via TS,
	 * this method is used to ensure that the keys we are trying to get from the store actually exist. In DEV mode, it
	 * will raise exception if any of the key is not found in the store, via hasOwnProperty check. This means the key
	 * can have the `undefined` value, but the key itself must exist to prove the intention of it being `undefined`
	 * rather than missing and forgotten about.
	 * @param keys to get from the store
	 * @param analyticsName
	 */
	checkStore(keys: string[], analyticsName?: string) {
		// #if [DEV]
		for (const key of keys) {
			const errors = [];
			if (!Object.prototype.hasOwnProperty.call(this.store, key)) {
				errors.push(key);
			}
			if (errors.length) {
				throw new Error(
					`Analytics ${analyticsName ? `${analyticsName} ` : ''}- Missing keys in DOTHelper.store: ${errors.join(', ')}`,
				);
			}
		}
		// #endif
	}

	setOfferDetailMeta(data: OfferDetailMeta) {
		const tab = this.store.tab;

		SessionStorage.setOfferDetailMeta({
			tab,
			...data,
		});
	}

	/**
	 * Converts `true`, `1` and `'1'` to `1`, else returns `0`.
	 * @param value
	 */
	sanitizeBoolean(value: any) {
		// The `==` is intentional, it works out of the box for true `1` and 1
		if (value == 1) {
			return 1;
		}
		// The `==` is intentional, it works out of the box for false '0' and 0
		if (value == 0) {
			return 0;
		}
		return 0;
	}
}

export const DOTHelper = new DOTClass();

if (typeof window !== 'undefined' && typeof window.historyStorage !== 'undefined') {
	window.addEventListener('spa', (event) => {
		DOTHelper.addToStore({
			prevPageType: event.detail.targetState.prevPageType,
			searchType: event.detail.targetState.prevPageType,
		});
	});

	window.historyStorage.mergePushStateData = (prePushData, newData) => {
		return {
			...pick(prePushData, 'scrollX', 'scrollY'),
			...(prePushData.pageType && { prevPageType: prePushData.pageType }),
			...newData,
		};
	};
}
